import React from 'react';
import {
  Col,
  Input,
  InputGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { FiEye, FiEyeOff } from 'react-icons/fi';

import { showSwal } from '../../../constants/swalConfig';
import ComponentCard from '../../../components/ComponentCard';
import BreadCrumbs from '../../../layouts/breadcrumbs/BreadCrumbs';
import api from '../../../constants/api';
import AppButton from '../../../components/common/AppButton';
import ActionButton from '../../../components/common/ActionButton';
import Text from '../../../components/common/Text';
import DataTable from '../../../components/common/DataTable';
import StatusBadge from '../../../components/common/StatusBadge';
import { isStaff } from '../../../constants/tenancy';
// No maxLength on the inputs: the browser would silently cut a pasted value,
// so an admin could hand out a password that is not the one saved. The rules
// below report "at most N characters" and block saving instead.
import {
  normalizeUsername,
  usernameError,
  passwordError,
} from './teamCredentials';

const EMPTY_TEAM = {
  name: '',
  username: '',
  password: '',
  employee_role: '',
  location: '',
  address: '',
  color: '#A2FF86',
 
};

const roleLabel = (role) => {
  if (role === 'employee') {
    return 'Employee';
  }

  if (role === 'workstation') {
    return 'Workstation';
  }

  return '';
};

const validateLocation = (location) => {
  const value = String(location || '').trim();

  if (!value) {
    return true;
  }

  const parts = value.split(',');

  if (parts.length !== 2) {
    return false;
  }

  const latitude = Number(parts[0].trim());
  const longitude = Number(parts[1].trim());

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return false;
  }

  if (latitude < -90 || latitude > 90) {
    return false;
  }

  if (longitude < -180 || longitude > 180) {
    return false;
  }

  return true;
};
export default function Index() {
  // HR Management: staff can view teams but not add, edit or delete them.
  const readOnly = isStaff();
  const [employess, setEmployees] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
const [originalTeam, setOriginalTeam] = React.useState(null);
  const [addOpen, setAddOpen] = React.useState(false);
  const [editingTeam, setEditingTeam] = React.useState(null);

  const [newTeam, setNewTeam] = React.useState(EMPTY_TEAM);
  // Live username availability: idle | checking | available | taken | error
  const [usernameCheck, setUsernameCheck] = React.useState({ status: 'idle', message: '' });
  const [touched, setTouched] = React.useState({});
  const [showPassword, setShowPassword] = React.useState(false);

  /*
   * Keep these declarations before any handlers that use them.
   * This avoids no-use-before-define lint errors.
   */
  const rowsPerPage = 15;
  const employeeRows = Array.isArray(employess) ? employess : [];

  const isEditMode = Boolean(editingTeam);

  // employee.username is the serviceman app login. On edit an unchanged
  // username is never re-validated or sent, so teams created before it existed
  // (username still empty) can be edited without setting one.
  const usernameChanged =
    !isEditMode ||
    normalizeUsername(newTeam.username) !== normalizeUsername(originalTeam?.username);
  const usernameFormatError = usernameChanged ? usernameError(newTeam.username) : null;
  const passwordFormatError = passwordError(newTeam.password, { required: !isEditMode });

  const reloadTeams = () =>
    api
      .get('/location/getAllEmployees')
      .then((res) => {
        setEmployees(Array.isArray(res?.data?.data) ? res.data.data : []);
      })
      .catch((err) => {
        console.log(err);
      });

  React.useEffect(() => {
    reloadTeams()
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const editingTeamId = editingTeam?.id;
  React.useEffect(() => {
    if (!addOpen || !usernameChanged || usernameFormatError) {
      setUsernameCheck({ status: 'idle', message: '' });
      return undefined;
    }
    setUsernameCheck({ status: 'checking', message: '' });
    let cancelled = false;
    const timer = setTimeout(() => {
      api
        .get('/driverhelper/checkUsername', {
          params: { username: normalizeUsername(newTeam.username), excludeId: editingTeamId },
        })
        .then((res) => {
          if (cancelled) return;
          const result = res?.data?.data || {};
          setUsernameCheck(
            result.available
              ? { status: 'available', message: 'Available' }
              : { status: 'taken', message: result.message || 'Username is not available.' },
          );
        })
        .catch(() => {
          // The API checks again on save, so a failed lookup does not block saving.
          if (!cancelled) setUsernameCheck({ status: 'error', message: 'Could not check the username right now.' });
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [addOpen, newTeam.username, usernameChanged, usernameFormatError, editingTeamId]);

  // First reason the credentials cannot be saved yet, or null.
  const credentialsProblem = () => {
    if (usernameFormatError) return usernameFormatError;
    if (usernameCheck.status === 'checking') return 'Still checking the username. Try again in a moment.';
    if (usernameCheck.status === 'taken') return usernameCheck.message;
    if (passwordFormatError) return passwordFormatError;
    return null;
  };

  const resetTeamForm = () => {
    setNewTeam(EMPTY_TEAM);
    setEditingTeam(null);
    setUsernameCheck({ status: 'idle', message: '' });
    setTouched({});
    setShowPassword(false);
  };

  const closeTeamModal = () => {
    if (saving) {
      return;
    }

    setAddOpen(false);
    resetTeamForm();
  };

  const openAddTeam = () => {
    resetTeamForm();
    setAddOpen(true);
  };

  const openEditTeam = (team) => {
  setEditingTeam(team);

  const teamData = {
    name: team?.name || '',
    username: team?.username || '',
    password: '',
    employee_role: team?.employee_role || '',
    location: team?.location || '',
    address: team?.address || '',
    color: team?.color || '#A2FF86',
    status: team?.status || 'on',
  };

  setOriginalTeam(teamData);
  setNewTeam(teamData);

  setAddOpen(true);
};

  const handleTeamInputChange = (event) => {
    const { name } = event.target;
    const value = name === 'username' ? event.target.value.toLowerCase() : event.target.value;

    setNewTeam((previous) => ({
      ...previous,
      [name]: value,
    }));
    setTouched((previous) => ({ ...previous, [name]: true }));
  };

 const handleAddTeam = async () => {
  if (!String(newTeam.name).trim()) {
    toast.error('Team name is required.');
    return;
  }

  if (!newTeam.employee_role) {
    toast.error('Employee Type is required.');
    return;
  }

  setTouched({ username: true, password: true });
  const credentialsIssue = credentialsProblem();
  if (credentialsIssue) {
    toast.error(credentialsIssue);
    return;
  }

  if (!String(newTeam.location).trim()) {
    toast.error('Location is required.');
    return;
  }

  if (!String(newTeam.address).trim()) {
    toast.error('Address is required.');
    return;
  }

  if (!String(newTeam.color).trim()) {
    toast.error('Colour is required.');
    return;
  }

  if (!validateLocation(newTeam.location)) {
    toast.error(
      'Please enter a valid location in latitude, longitude format.',
    );
    return;
  }

  setSaving(true);

  try {
    const response = await api.post('/driverhelper/addEmployee', {
      name: String(newTeam.name).trim(),
      employee_role: newTeam.employee_role,
      location: newTeam.location.trim(),
      address: newTeam.address.trim(),
      color: newTeam.color,
      username: normalizeUsername(newTeam.username),
      password: newTeam.password,
    });

    toast.success('Team created successfully.');

    const createdTeamName =
      response?.data?.data?.name || newTeam.name.trim();

    setAddOpen(false);
    resetTeamForm();

    await reloadTeams();

    showSwal({
      title: 'Team Created Successfully',
      text: `Team ${createdTeamName} has been created. Add employees to this team.`,
      icon: 'success',
      showCancelButton: true,
      confirmButtonText: 'Add Employees',
      cancelButtonText: 'Later',
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.hash = '/driverHelper';
      }
    });
  } catch (err) {
    const backendMessage =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      'Unable to create team.';

    toast.error(backendMessage);
  } finally {
    setSaving(false);
  }
};

 const handleUpdateTeam = async () => {
  if (!editingTeam?.id) {
    toast.error('Employee ID is missing.');
    return;
  }

  if (!String(newTeam.name).trim()) {
    toast.error('Team name is required.');
    return;
  }

  if (!newTeam.employee_role) {
    toast.error('Employee Type is required.');
    return;
  }

  setTouched({ username: true, password: true });
  const credentialsIssue = credentialsProblem();
  if (credentialsIssue) {
    toast.error(credentialsIssue);
    return;
  }

  if (!validateLocation(newTeam.location)) {
    toast.error(
      'Please enter a valid location in latitude, longitude format.',
    );
    return;
  }

const hasChanges =
  String(newTeam.name).trim() !== String(originalTeam.name).trim() ||
  newTeam.employee_role !== originalTeam.employee_role ||
  String(newTeam.location).trim() !== String(originalTeam.location).trim() ||
  String(newTeam.address).trim() !== String(originalTeam.address).trim() ||
  newTeam.color !== originalTeam.color ||
  newTeam.status !== originalTeam.status ||
  usernameChanged ||
  newTeam.password !== '';

  if (!hasChanges) {
    toast.info('No changes made to update.');
    return;
  }

  setSaving(true);

  try {
    const response = await api.put(
      `/driverhelper/updateEmployee/${editingTeam.id}`,
      {
        name: String(newTeam.name).trim(),
        location: newTeam.location.trim(),
        address: newTeam.address.trim(),
        color: newTeam.color,
        employee_role: newTeam.employee_role,
         status: newTeam.status,
        ...(usernameChanged ? { username: normalizeUsername(newTeam.username) } : {}),
        ...(newTeam.password ? { password: newTeam.password } : {}),
      },
    );

    toast.success(
      response?.data?.message || 'Employee updated successfully',
    );

    setAddOpen(false);
    resetTeamForm();

    await reloadTeams();
  } catch (err) {
    const backendMessage =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      'Unable to update employee.';

    toast.error(backendMessage);
  } finally {
    setSaving(false);
  }
};
  const handleSaveTeam = () => {
    if (isEditMode) {
      handleUpdateTeam();
    } else {
      handleAddTeam();
    }
  };

  const handleDeleteTeam = (team) => {
  if (!team?.id) {
    toast.error('Employee ID is missing.');
    return;
  }

  showSwal(
    {
      title: 'Are you sure?',
      text: `You want to delete ${team.name || 'this team'}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
    },
    'delete',
  ).then((result) => {
    if (!result.isConfirmed) {
      return;
    }

    api
      .delete(`/driverhelper/deleteEmployee/${team.id}`)
      .then((response) => {
        toast.success(
          response?.data?.message || 'Employee deleted successfully',
        );

        setEmployees((previous) =>
          previous.filter((employee) => employee.id !== team.id),
        );
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            'Unable to delete employee.',
        );
      });
  });
};

  return (
    <>
      <BreadCrumbs />

      <Row>
        <Col md="12">
          <ComponentCard>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Text variant="cardTitle" tag="h5">
                Teams
              </Text>

              {!readOnly && (
                <AppButton
                  label="+ Add Team"
                  variant="buttonColor"
                  onClick={openAddTeam}
                />
              )}
            </div>

            {/* CREATE / EDIT TEAM MODAL */}
            <Modal
              isOpen={addOpen}
              toggle={closeTeamModal}
              centered
              className="teams-edit-modal"
              style={{
                maxWidth: '560px',
                width: 'calc(100% - 30px)',
              }}
            >
              <ModalHeader toggle={closeTeamModal}>
                {isEditMode ? 'Edit Team' : 'Add Team'}
              </ModalHeader>

              <ModalBody
                className="teams-edit-modal-body"
                style={{
                  maxHeight: 'calc(100vh - 220px)',
                  overflowY: 'auto',
                  padding: '20px 24px',
                }}
              >
                <Row>
                  <Col md="6">
                    <div className="bk-field mb-3">
                      <Label for="team-name">
                        Team Name
                        <span className="req">*</span>
                      </Label>

                      <Input
                        id="team-name"
                        name="name"
                        className="form-control"
                        value={newTeam.name}
                        placeholder="e.g. 218"
                        onChange={handleTeamInputChange}
                      />

                      {!isEditMode && (
                        <small className="text-muted">
                          This is the team name shown in slot search and
                          Assigned To.
                        </small>
                      )}
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="bk-field mb-3">
                      <Label for="team-role">
                        Employee Type
                        <span className="req">*</span>
                      </Label>

                      <Input
                        id="team-role"
                        name="employee_role"
                        type="select"
                        className="form-control"
                        value={newTeam.employee_role}
                        onChange={handleTeamInputChange}
                      >
                        <option value="">Select Employee Type</option>
                        <option value="employee">Employee</option>
                        <option value="workstation">Workstation</option>
                      </Input>


                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <div className="bk-field mb-3">
                      <Label for="team-username">
                        Username
                        {!isEditMode && <span className="req">*</span>}
                      </Label>

                      <Input
                        id="team-username"
                        name="username"
                        className="form-control"
                        value={newTeam.username}
                        placeholder="e.g. rahul.k"
                        autoComplete="off"
                        autoCapitalize="none"
                        spellCheck={false}
                        invalid={(touched.username && !!usernameFormatError) || usernameCheck.status === 'taken'}
                        valid={usernameCheck.status === 'available'}
                        onChange={handleTeamInputChange}
                      />

                      <small
                        data-testid="username-feedback"
                        className={
                          (touched.username && usernameFormatError) || usernameCheck.status === 'taken'
                            ? 'text-danger d-block'
                            : usernameCheck.status === 'available'
                              ? 'text-success d-block'
                              : 'text-muted d-block'
                        }
                      >
                        {(touched.username && usernameFormatError) ||
                          (usernameCheck.status === 'checking' && 'Checking…') ||
                          (usernameCheck.status === 'available' && '✓ Available') ||
                          ((usernameCheck.status === 'taken' || usernameCheck.status === 'error') && usernameCheck.message) ||
                          'Used to log in to the serviceman app. Letters, numbers, . _ -'}
                      </small>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="bk-field mb-3">
                      <Label for="team-password">
                        {isEditMode ? 'New Password' : 'Password'}
                        {!isEditMode && <span className="req">*</span>}
                      </Label>

                      <InputGroup>
                        <Input
                          id="team-password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          className="form-control"
                          value={newTeam.password}
                          placeholder={isEditMode ? 'Leave blank to keep current' : 'At least 6 characters'}
                          autoComplete="new-password"
                          invalid={touched.password && !!passwordFormatError}
                          onChange={handleTeamInputChange}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onClick={() => setShowPassword((previous) => !previous)}
                        >
                          {showPassword ? <FiEyeOff /> : <FiEye />}
                        </button>
                      </InputGroup>

                      <small
                        data-testid="password-feedback"
                        className={touched.password && passwordFormatError ? 'text-danger d-block' : 'text-muted d-block'}
                      >
                        {(touched.password && passwordFormatError) ||
                          (isEditMode ? 'Leave blank to keep the current password.' : '6–32 characters, no spaces.')}
                      </small>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <div className="bk-field mb-3">
                      <Label for="team-location">
                          Base Location (lat, long)
                          <span className="req">*</span>
                        </Label>

                      <Input
                        id="team-location"
                        name="location"
                        className="form-control"
                        value={newTeam.location}
                        placeholder="18.5679, 73.9143"
                        onChange={handleTeamInputChange}
                      />

                      <small className="text-muted">
                         Enter latitude and longitude separated by a comma.
                      </small>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="bk-field mb-3">
                     <Label for="team-address">
                      Address
                      <span className="req">*</span>
                    </Label>

                      <Input
                        id="team-address"
                        name="address"
                        className="form-control"
                        value={newTeam.address}
                        placeholder="Team base address"
                        onChange={handleTeamInputChange}
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md="4">
                    <div className="bk-field mb-3">
                      <Label for="team-color">
                        Colour
                        <span className="req">*</span>
                      </Label>

                      <Input
                        id="team-color"
                        name="color"
                        type="color"
                        className="form-control"
                        value={newTeam.color || '#A2FF86'}
                        onChange={handleTeamInputChange}
                        style={{
                          height: '38px',
                          padding: '3px',
                        }}
                      />
                    </div>
                  </Col>

                  {isEditMode && (
                    <Col md="4">
                      <div className="bk-field mb-3">
                        <Label for="team-status">
                          Status
                        </Label>

                        <Input
                          id="team-status"
                          name="status"
                          type="select"
                          className="form-control"
                          value={newTeam.status || 'on'}
                          onChange={handleTeamInputChange}
                        >
                          <option value="on">Active</option>
                          <option value="off">Inactive</option>
                        </Input>
                      </div>
                    </Col>
                  )}
                </Row>
              </ModalBody>

              <ModalFooter className="teams-edit-modal-footer d-flex justify-content-end gap-2">
                <AppButton
                  label="Cancel"
                 
                  outline
                  type="button"
                  onClick={closeTeamModal}
                  disabled={saving}
                />

                <AppButton
                  label={
                    saving
                      ? isEditMode
                        ? 'Updating...'
                        : 'Saving...'
                      : isEditMode
                        ? 'Update Team'
                        : 'Create Team'
                  }
                  variant="buttonColor"
                  type="button"
                  onClick={handleSaveTeam}
                  disabled={saving}
                />
              </ModalFooter>
            </Modal>

            {/* TEAMS TABLE */}
            <DataTable
              columns={[
                { key: 'number', header: 'S.No', render: (row, index) => index + 1 },
                { key: 'name', header: 'Name', render: (row) => row.name || '' },
                { key: 'username', header: 'Username', render: (row) => row.username || '' },
                { key: 'role', header: 'Role', render: (row) => roleLabel(row.employee_role) },
                { key: 'location', header: 'Location', render: (row) => row.location || '' },
                { key: 'address', header: 'Address', render: (row) => row.address || '' },
                // { key: 'color', header: 'Color', render: (row) => row.color || '' },
                {
                  key: 'preview',
                  header: 'Preview',
                  render: (row) => (
                    <div
                      style={{
                        backgroundColor: row.color || '#000',
                        width: '42px',
                        height: '42px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                      }}
                    />
                  ),
                },
  {
  key: 'status',
  header: 'Status',
  render: (row) => (
    <StatusBadge
      variant={row.status === 'on' ? 'success' : 'danger'}
      label={row.status === 'on' ? 'Active' : 'Inactive'}
    />
  ),
},
                ...(readOnly ? [] : [{
                  key: 'action',
                  header: 'Action',
                  render: (row) => (
                    <div className="d-flex align-items-center gap-2">
                      <ActionButton action="edit" onClick={() => openEditTeam(row)} />
                      <ActionButton action="delete" onClick={() => handleDeleteTeam(row)} />
                    </div>
                  ),
                }]),
              ]}
              data={employeeRows}
              loading={loading}
              pageSize={rowsPerPage}
              searchable={false}
              emptyMessage="No employees found"
              rowKey={(row) => row.id}
            />
          </ComponentCard>
        </Col>
      </Row>
    </>
  );
}
